import React from 'react';

import { MatchStatusType } from '../../../typings/match';
import { getMatchStatusLabel, isLive, isNotEnded } from '../../../utils/match-status';
import { getMappedColor, makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import IconPlay from '../../atoms/icons/icon-play';
import MatchStatus, { IMatchStatus } from '../../atoms/match-status/match-status';
import Text from '../../atoms/text/text';
import { IMatchRow } from '../match-row/match-row';
import { ITeam } from '../team/team';

import { colorSchemaMapping, DEFAULT_COLOR, MatchChangeVariant } from './match-change-common';
import { getGoalByStatus, getGoalsText, getTeam } from './match-change-helpers';

export const MATCH_CHANGE_TYPE_HP = 'hp';
interface IMatchChangeTeam
  extends Pick<
    ITeam,
    | 'id'
    | 'goal'
    | 'logoUrl'
    | 'name'
    | 'overtimeGoal'
    | 'shootoutGoal'
    | 'totalGoalLegs'
    | 'totalMatchGoal'
    | 'regularTimeGoal'
  > {
  // totalMatchGoals: number; // REGULAR TIME + OVERTIME + SHOOTOUTS
}
interface IMatchChangeRow
  extends Pick<
    IMatchRow,
    | 'color'
    | 'colorSchema'
    | 'competitionPhase'
    | 'competitionPhaseRound'
    | 'competitionPhaseWinnerTeamId'
    | 'customCompetitionName'
    | 'date'
    | 'id'
    | 'link'
    | 'status'
    | 'target'
    | 'trackingData'
  > {
  hasVideoHighlights?: boolean;
  away: IMatchChangeTeam;
  home: IMatchChangeTeam;
}
export interface IMatchChangeHp extends IMatchChangeRow {
  variant?: MatchChangeVariant;
}

export const classNames = (status: MatchStatusType) =>
  makeClassNames({
    wrapper: ['match-change--wrapper', `match-change--wrapper-${MATCH_CHANGE_TYPE_HP}`],
    rowCompetition: 'match-change-row--competition',
    rowStatus: 'match-change-row--status',
    rowTeam: 'match-change-row--team',
    cellStatus: ['match-change-cell--status', `match-change-cell--status-${status.toLowerCase()}`],
    cellLiveOrFulltime: 'match-change-cell--fulltime',
    cellVideo: 'match-change-cell--video',
    cellTeam: 'match-change-cell--team',
    cellGoal: 'match-change-cell--goal',
  });

export const MatchChangeHp = ({
  /* istanbul ignore next: default value */ hasVideoHighlights = false,
  /* istanbul ignore next: default value */ trackingData = {},
  /* istanbul ignore next: default value */ color = DEFAULT_COLOR,
  away,
  colorSchema,
  competitionPhase,
  competitionPhaseRound,
  // competitionPhaseWinnerTeamId, -> UNUSED actually
  customCompetitionName,
  date,
  home,
  id,
  link,
  status,
  target,
}: IMatchChangeHp) => {
  const cn = classNames(status);
  const colorToUse = getMappedColor({ colorSchema, colorSchemaMapping, defaultColor: color });

  // MATCH STATUS row
  const argsStatus: IMatchStatus = {
    backgroundColor: true,
    blinking: true,
    chrono: false,
    label: getMatchStatusLabel(status),
    live: isLive(status),
    red: isNotEnded(status),
  };
  const getMatchStatusRow = (matchStatus: MatchStatusType) =>
    matchStatus === 'PreMatch' ? (
      <Text fontSize="12" fontWeight="400" color={colorToUse}>
        {dateFormat({ date, format: 'DD MMM, hh:mm' })}
      </Text>
    ) : (
      <MatchStatus {...argsStatus} />
    );

  const { /* istanbul ignore next: default value */ goal: homeGoal = '-', name: homeName, logoUrl: homeLogo } = home;
  const { /* istanbul ignore next: default value */ goal: awayGoal = '-', name: awayName, logoUrl: awayLogo } = away;

  return (
    <Text
      tag="a"
      href={link ?? undefined}
      target={target}
      dataAttributes={trackingData}
      className={cn.wrapper}
      color={colorToUse}
    >
      <div className={cn.rowCompetition}>
        <Text fontSize="12" fontWeight="500" color={colorToUse}>
          {customCompetitionName}
        </Text>
      </div>
      <div className={cn.rowStatus}>
        <div className={cn.cellStatus}>{getMatchStatusRow(status)}</div>
        <div className={cn.cellLiveOrFulltime}>
          <Text fontSize="12" fontWeight="400" color={colorToUse}>
            {getGoalsText({ competitionPhase, competitionPhaseRound, status, away, home })}
          </Text>
        </div>
        {hasVideoHighlights && (
          <div className={cn.cellVideo}>
            <IconPlay size="xsmall" color="light" />
          </div>
        )}
      </div>
      <div className={cn.rowTeam}>
        <div className={cn.cellTeam}>{getTeam(homeName, homeLogo ?? '')}</div>
        <div className={cn.cellGoal}>{getGoalByStatus(homeGoal, status)}</div>
      </div>
      <div className={cn.rowTeam}>
        <div className={cn.cellTeam}>{getTeam(awayName, awayLogo ?? '')}</div>
        <div className={cn.cellGoal}>{getGoalByStatus(awayGoal, status)}</div>
      </div>
    </Text>
  );
};
